import { addDaysToIsoDate, getTimeZoneDayBoundsUtc, isoDateToUtcLabelDate, zonedDateTimeToUtc } from '@/lib/dates/timezoneDayRange'

export const BUSINESS_TIMEZONE = 'Asia/Karachi'

const DATE_ONLY_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIMEZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIMEZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

function parseIsoDateStrict(isoDate: string): { year: number; month: number; day: number } | null {
  const m = String(isoDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))
  if (d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month || d.getUTCDate() !== day) return null
  return { year, month, day }
}

function datePartsInBusinessTimezone(input: Date): { year: string; month: string; day: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(input)

  const byType: Record<string, string> = {}
  for (const part of parts) byType[part.type] = part.value

  return {
    year: byType.year,
    month: byType.month,
    day: byType.day,
  }
}

function tryParseDateLikeInput(input: string): Date | null {
  const s = input.trim()
  if (!s) return null

  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    const year = Number(iso[1])
    const month = Number(iso[2])
    const day = Number(iso[3])
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      const localNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))
      if (!Number.isNaN(localNoon.getTime())) return localNoon
    }
  }

  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) return parsed
  return null
}

const HAS_EXPLICIT_TIMEZONE = /(z|[+-]\d{2}:?\d{2})$/i

function isValidIsoDateParts(year: number, month: number, day: number): boolean {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const probe = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))
  return probe.getUTCFullYear() === year && probe.getUTCMonth() + 1 === month && probe.getUTCDate() === day
}

function buildBusinessZonedDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): Date | null {
  if (!isValidIsoDateParts(year, month, day)) return null
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) return null
  return zonedDateTimeToUtc(year, month, day, hour, minute, second, 0, BUSINESS_TIMEZONE)
}

export interface ParseBusinessDateTimeOptions {
  preferDayFirst?: boolean
}

/**
 * Parse date-time strings where missing timezone should be interpreted as Asia/Karachi.
 * Keeps explicit-offset timestamps as-is.
 */
export function parseBusinessDateTimeInput(
  input: string | null | undefined,
  options: ParseBusinessDateTimeOptions = {}
): Date | null {
  const raw = String(input ?? '').trim()
  if (!raw) return null

  if (HAS_EXPLICIT_TIMEZONE.test(raw)) {
    const parsed = new Date(raw)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const ymd = raw.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2})(?::(\d{2})(?::(\d{2}))?)?)?$/)
  if (ymd) {
    return buildBusinessZonedDate(
      Number(ymd[1]),
      Number(ymd[2]),
      Number(ymd[3]),
      Number(ymd[4] ?? 0),
      Number(ymd[5] ?? 0),
      Number(ymd[6] ?? 0),
    )
  }

  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2})(?::(\d{2})(?::(\d{2}))?)?)?$/)
  if (dmy) {
    const first = Number(dmy[1])
    const second = Number(dmy[2])
    const year = Number(dmy[3])
    const preferDayFirst = options.preferDayFirst !== false

    let day: number
    let month: number
    if (first > 12 && second <= 12) {
      day = first
      month = second
    } else if (second > 12 && first <= 12) {
      day = second
      month = first
    } else if (preferDayFirst) {
      day = first
      month = second
    } else {
      day = second
      month = first
    }

    return buildBusinessZonedDate(
      year,
      month,
      day,
      Number(dmy[4] ?? 0),
      Number(dmy[5] ?? 0),
      Number(dmy[6] ?? 0),
    )
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function toBusinessDateString(input: Date | string | null | undefined): string {
  if (input == null) return ''

  if (typeof input === 'string') {
    const strict = parseIsoDateStrict(input)
    if (strict) {
      const mm = String(strict.month).padStart(2, '0')
      const dd = String(strict.day).padStart(2, '0')
      return `${strict.year}-${mm}-${dd}`
    }
  }

  const date = typeof input === 'string' ? tryParseDateLikeInput(input) : new Date(input)
  if (!date || Number.isNaN(date.getTime())) return ''

  const parts = datePartsInBusinessTimezone(date)
  return `${parts.year}-${parts.month}-${parts.day}`
}

/**
 * Best-effort YYYY-MM-DD in Asia/Karachi. Uses Date/Intl for non-strict inputs.
 * For validated API books dates, prefer {@link parseDateFrom} / strict YMD + {@link getBusinessDayBoundsUtc}
 * so the stored label matches the request string.
 */
export function normalizeBusinessDateInput(input: Date | string | null | undefined): string {
  return toBusinessDateString(input)
}

export function getBusinessTodayDateString(now: Date = new Date()): string {
  return toBusinessDateString(now)
}

/** Today's calendar YYYY-MM-DD in a specific IANA timezone (e.g. Shopify store TZ). */
export function getTodayYmdInTimeZone(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(now)
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {} as Record<string, string>)
  return `${parts.year}-${parts.month}-${parts.day}`
}

/** Calendar YYYY-MM-DD for an instant in a specific IANA timezone. */
export function toCalendarDateStringInTimeZone(input: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(input)
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {} as Record<string, string>)
  return `${parts.year}-${parts.month}-${parts.day}`
}

export function getBusinessYesterdayDateString(now: Date = new Date()): string {
  const today = getBusinessTodayDateString(now)
  return addDaysToIsoDate(today, -1)
}

export function getBusinessMonthStartDateString(now: Date = new Date()): string {
  const today = getBusinessTodayDateString(now)
  const parsed = parseIsoDateStrict(today)
  if (!parsed) return today
  const mm = String(parsed.month).padStart(2, '0')
  return `${parsed.year}-${mm}-01`
}

export function getBusinessDateOffset(days: number, anchorIsoDate?: string): string {
  const anchor = anchorIsoDate || getBusinessTodayDateString()
  return addDaysToIsoDate(anchor, days)
}

export function getBusinessDayBoundsUtc(isoDate: string): { startUtc: Date; endUtc: Date } {
  return getTimeZoneDayBoundsUtc(isoDate, BUSINESS_TIMEZONE)
}

/**
 * Returns a UTC-midnight label Date for a PKT business-date string (YYYY-MM-DD).
 * IMPORTANT: This is a label convention, NOT the actual PKT day-start timestamp.
 * For "2026-03-15" (PKT business date), returns 2026-03-15T00:00:00.000Z. The actual
 * PKT day start in UTC is 2026-03-14T19:00:00.000Z. Use this for StoreUsage, ShopDailyMetrics,
 * and related tables that store per-day rows keyed by label date.
 */
export function toUtcLabelDateForBusinessDate(isoDate: string): Date {
  return isoDateToUtcLabelDate(isoDate)
}

/**
 * Returns the UTC-midnight label Date for today's PKT business date.
 * Used by StoreUsage and webhook idempotency for per-day upserts.
 * IMPORTANT: Label convention only — not the actual PKT day-start timestamp.
 */
export function todayBusinessLabelDate(now: Date = new Date()): Date {
  return toUtcLabelDateForBusinessDate(getBusinessTodayDateString(now))
}

export function parseBusinessDateStartUtc(input: Date | string | null | undefined): Date | null {
  const iso = normalizeBusinessDateInput(input)
  if (!iso) return null
  return getBusinessDayBoundsUtc(iso).startUtc
}

export function parseBusinessDateEndUtc(input: Date | string | null | undefined): Date | null {
  const iso = normalizeBusinessDateInput(input)
  if (!iso) return null
  return getBusinessDayBoundsUtc(iso).endUtc
}

export function formatBusinessDate(input: Date | string | null | undefined): string {
  if (input == null) return '—'
  if (typeof input === 'string') {
    const strict = parseIsoDateStrict(input.trim())
    if (strict) {
      // Avoid new Date("YYYY-MM-DD") (implementation-dependent UTC vs local).
      const date = new Date(Date.UTC(strict.year, strict.month - 1, strict.day, 12, 0, 0, 0))
      if (Number.isNaN(date.getTime())) return '—'
      return DATE_ONLY_FORMATTER.format(date)
    }
  }
  const date = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return '—'
  return DATE_ONLY_FORMATTER.format(date)
}

export function formatBusinessDateTime(input: Date | string | null | undefined): string {
  if (input == null) return '—'
  const date = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return '—'
  return DATE_TIME_FORMATTER.format(date)
}

export function formatBusinessDateCustom(
  input: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-GB'
): string {
  if (input == null) return '—'
  const date = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    timeZone: BUSINESS_TIMEZONE,
    ...options,
  }).format(date)
}

export function getBusinessDefaultRange(defaultDaysBack = 30, now: Date = new Date()): {
  from: string
  to: string
  fromUtc: Date
  toUtc: Date
} {
  const to = getBusinessTodayDateString(now)
  const from = addDaysToIsoDate(to, -Math.max(0, defaultDaysBack))
  const fromUtc = getBusinessDayBoundsUtc(from).startUtc
  const toUtc = getBusinessDayBoundsUtc(to).endUtc
  return { from, to, fromUtc, toUtc }
}

export function getBusinessDatePresets(now: Date = new Date()): {
  today: string
  yesterday: string
  last7From: string
  thisMonthFrom: string
  thisMonthTo: string
} {
  const today = getBusinessTodayDateString(now)
  return {
    today,
    yesterday: addDaysToIsoDate(today, -1),
    last7From: addDaysToIsoDate(today, -6),
    thisMonthFrom: getBusinessMonthStartDateString(now),
    thisMonthTo: today,
  }
}

export function businessIsoDateToPickerDate(isoDate: string): Date {
  const parsed = parseIsoDateStrict(isoDate)
  if (!parsed) return new Date()
  return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12, 0, 0, 0))
}
